import { FC, useCallback, useMemo } from 'react';
import { SwiperSlide } from 'swiper/react';
import { IImage } from 'shared/models/media';
import { Image } from '~/components/image';
import { useAtomValue, useSetAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { $SimpleCTAModalShow } from '~/models/portal/simple-cta-modal';
import { ClickPosition } from '~/utils/click-position';
import { $IsMobileUser } from '~/utils/is-mobile-user';
import { $CurrentGalleryArticle, $LabelVisible, $RootPaddingBottom } from '~/components/article-gallery/atoms';
import { useScopedAtomValue } from '@lemon8/webapp-shared/component';
import { GalleryLabel } from '../gallery-label';
import { useActiveToArticle } from '~/utils/active/use-active-to-article';
import { $UserAgent } from '@lemon8/webapp-shared';
import { $HitSEOImgOCR } from '~/utils/libra/seo-img-ocr';

import './style.scss';

interface IGallerySlideProps {
  image: IImage & { imgOcrText: string; imgAutoGenDesc: string };
  index: number;
}

export const GallerySlide: FC<IGallerySlideProps> = (props) => {
  const { image, index } = props;
  const { t } = useTranslation();
  const article = useScopedAtomValue($CurrentGalleryArticle);
  const { title, galleryLabels, author } = article;
  const { nickName = '' } = author;
  const labelVisible = useScopedAtomValue($LabelVisible);
  const isMobileUser = useAtomValue($IsMobileUser);
  const setSimpleCTAModal = useSetAtom($SimpleCTAModalShow);
  const hitSEOImgOCR = useAtomValue($HitSEOImgOCR);
  const activeToArticle = useActiveToArticle(article);
  const rootPaddingBottom = useScopedAtomValue($RootPaddingBottom);
  const { isBot } = useAtomValue($UserAgent);

  const { imgAutoGenDesc, imgOcrText } = image;

  const showCaption = hitSEOImgOCR && isBot && imgOcrText;

  const imageHeight = showCaption ? '95%' : '100%';

  const currentSlideLabels = galleryLabels.filter((g) => g.imageIndex === index);

  const style = useMemo(() => {
    const { width, height } = image;
    const aspectRadio = width / height;
    const rootAspectRatio = 100 / rootPaddingBottom;

    const scale = Math.min(aspectRadio / rootAspectRatio, rootAspectRatio / aspectRadio);

    return {
      width: `${100 * scale}%`,
      height: `${(100 * scale) / aspectRadio}%`,
    };
  }, [rootPaddingBottom]);

  const handleClick = useCallback(() => {
    if (isMobileUser) {
      window.Tea('wap_click', {
        current_position: ClickPosition.gallery_img_open,
      });
      setSimpleCTAModal({
        currentPosition: ClickPosition.gallery_img_open_modal,
        body: t('seo_stopVideo_modal_desc'),
        handleClickConfirm: () => activeToArticle(ClickPosition.gallery_img_open_modal),
      });
    }
  }, [activeToArticle]);

  return (
    <SwiperSlide>
      <div className="gallery-swiper-item">
        <div className="gallery-swiper-item-inner" onClick={handleClick} style={style}>
          <div className="gallery-swiper-item-image" style={{ height: imageHeight }}>
            <Image
              src={image.url}
              alt={
                hitSEOImgOCR
                  ? imgAutoGenDesc || t('own_picture_with_count').replace('%s', title).replace('%d', String(index))
                  : t('own_picture_with_count').replace('%s', title).replace('%d', String(index))
              }
            />
            <div className="gallery-swiper-item-image-label-wrapper">
              {currentSlideLabels.map((label, index) => (
                <GalleryLabel key={`${label.id}-${index}`} label={label} show={labelVisible} />
              ))}
            </div>
          </div>
          {showCaption && (
            <div className="image-ocr-text">{`${t('seo_keyword_page_postedBy')} ${nickName}. ${imgOcrText}`}</div>
          )}
        </div>
      </div>
    </SwiperSlide>
  );
};

GallerySlide.displayName = SwiperSlide.displayName;
